/**
 * EditUserModal Component
 *
 * Modal form for editing an existing user.
 */

import React, { useState, useEffect } from 'react';
import { X, User, Mail, Lock, Building2, Shield, CreditCard, AlertCircle, AlertTriangle } from 'lucide-react';
import type { AdminUser, UpdateUserData, UserRole, SubscriptionTier, UserStatus, AdminCompany, RBACRole } from '../../services/adminApi';
import { superAdminApi } from '../../services/adminApi';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: number, data: UpdateUserData) => Promise<void>;
  user: AdminUser | null;
  companies: AdminCompany[];
  loadingCompanies?: boolean;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  companies,
  loadingCompanies = false,
}) => {
  const [formData, setFormData] = useState<UpdateUserData>({});
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [emailChangeConfirmed, setEmailChangeConfirmed] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [availableRoles, setAvailableRoles] = useState<RBACRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Load RBAC roles when modal opens
  useEffect(() => {
    if (isOpen) {
      setLoadingRoles(true);
      superAdminApi.getAllRoles()
        .then((roles) => {
          setAvailableRoles(roles);
        })
        .catch((error) => {
          console.error('Failed to load roles:', error);
        })
        .finally(() => {
          setLoadingRoles(false);
        });
    }
  }, [isOpen]);

  // Initialize form when user changes
  useEffect(() => {
    if (user && isOpen) {
      // Prefer RBAC role (rbacRole) over legacy role field
      const currentRole = (user.rbacRole || user.role || 'viewer') as UserRole;
      setFormData({
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: currentRole,
        companyId: user.companyId,
        subscriptionTier: user.subscriptionTier,
        status: user.status,
      });
      setOriginalEmail(user.email);
      setNewPassword('');
      setShowPasswordField(false);
      setEmailChangeConfirmed(false);
      setErrors({});
      setSubmitError(null);
    }
  }, [user, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.email !== originalEmail && !emailChangeConfirmed) {
      newErrors.email = 'Please confirm you want to change the email address';
    }

    if (showPasswordField && newPassword && newPassword.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData.role !== 'super_admin' && !formData.companyId) {
      newErrors.companyId = 'Company is required for non-super admin users';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm() || !user) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData: UpdateUserData = { ...formData };
      if (showPasswordField && newPassword) {
        updateData.password = newPassword;
      }
      await onSubmit(user.id, updateData);
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'companyId' ? (value ? parseInt(value, 10) : undefined) : value,
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  if (!isOpen || !user) return null;

  const emailChanged = formData.email !== originalEmail;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform rounded-2xl bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Edit User
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {submitError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* User ID info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">User ID</p>
                <p className="text-sm font-medium text-gray-900">#{user.id}</p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    placeholder="user@example.com"
                    className={`
                      w-full pl-10 pr-4 py-2 border rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                    `}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                )}
                {emailChanged && !emailChangeConfirmed && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-yellow-700">
                          Changing email address will affect the user&apos;s login credentials.
                        </p>
                        <label className="flex items-center gap-2 mt-1">
                          <input
                            type="checkbox"
                            checked={emailChangeConfirmed}
                            onChange={(e) => setEmailChangeConfirmed(e.target.checked)}
                            className="rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500"
                          />
                          <span className="text-xs text-yellow-700 font-medium">
                            I confirm this change
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                {!showPasswordField ? (
                  <button
                    type="button"
                    onClick={() => setShowPasswordField(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Change password
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="New password (min 8 characters)"
                        className={`
                          w-full pl-10 pr-4 py-2 border rounded-lg text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                          ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                        `}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordField(false);
                        setNewPassword('');
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel password change
                    </button>
                    {errors.password && (
                      <p className="text-xs text-red-500">{errors.password}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Name fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName || ''}
                    onChange={handleChange}
                    placeholder="John"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName || ''}
                    onChange={handleChange}
                    placeholder="Doe"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    name="role"
                    value={formData.role || 'user'}
                    onChange={handleChange}
                    disabled={loadingRoles}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingRoles ? (
                      <option value="">Loading roles...</option>
                    ) : availableRoles.length === 0 ? (
                      <option value="">No roles available</option>
                    ) : (
                      availableRoles.map((role) => (
                        <option key={role.id} value={role.name}>
                          {role.display_name} ({role.permission_count || 0} permissions)
                        </option>
                      ))
                    )}
                  </select>
                </div>
                {formData.role && formData.role !== (user?.rbacRole || user?.role) && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-700">
                        Changing role will update user permissions from {availableRoles.find(r => r.name === (user?.rbacRole || user?.role))?.permission_count || 0} to {availableRoles.find(r => r.name === formData.role)?.permission_count || 0} permissions.
                      </p>
                    </div>
                  </div>
                )}
                {availableRoles.length > 0 && formData.role && (
                  <p className="mt-1 text-xs text-gray-500">
                    {availableRoles.find(r => r.name === formData.role)?.description || ''}
                  </p>
                )}
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company {formData.role !== 'super_admin' && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    name="companyId"
                    value={formData.companyId || ''}
                    onChange={handleChange}
                    disabled={loadingCompanies || formData.role === 'super_admin'}
                    className={`
                      w-full pl-10 pr-4 py-2 border rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      appearance-none bg-white
                      ${formData.role === 'super_admin' ? 'bg-gray-100 text-gray-500' : ''}
                      ${errors.companyId ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                    `}
                  >
                    <option value="">
                      {loadingCompanies ? 'Loading companies...' : 'Select a company'}
                    </option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.companyId && (
                  <p className="mt-1 text-xs text-red-500">{errors.companyId}</p>
                )}
                {formData.role === 'super_admin' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Super admins are not assigned to a company
                  </p>
                )}
              </div>

              {/* Subscription Tier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subscription Tier
                </label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    name="subscriptionTier"
                    value={formData.subscriptionTier || 'free'}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status || 'active'}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
